import type { ReactNode } from 'react'
import { cx } from './cx'

export type StatTone = 'default' | 'warning' | 'muted'

const VALUE_TONES: Record<StatTone, string> = {
  default: 'text-brand-900',
  /** Bronze, for a figure that is a problem rather than a result. */
  warning: 'text-accent-700',
  muted: 'text-ink-500',
}

/**
 * One headline figure with its label.
 *
 * A figure with no caption gets misread, and a financial figure with no caption
 * gets misquoted, so `label` and `value` are both required and `hint` is where
 * the caveat goes — what the number excludes, or what it assumes.
 */
export function Stat({
  label,
  value,
  hint,
  tone = 'default',
  className,
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  tone?: StatTone
  className?: string
}) {
  return (
    <div
      className={cx(
        'rounded-lg border border-ink-200 bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(21,26,108,0.04)]',
        className
      )}
    >
      <p className="text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
        {label}
      </p>
      <p
        className={cx(
          'mt-1 font-display text-xl font-semibold tabular-nums',
          VALUE_TONES[tone]
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-xs leading-snug text-ink-600">{hint}</p>}
    </div>
  )
}

/** A row of stats that wraps rather than shrinking the figures. */
export function StatRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('grid gap-3 sm:grid-cols-2 lg:grid-cols-4', className)}>{children}</div>
  )
}
